import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { PHOTO_GALLERY } from "../data/site";

export const PhotoGallery = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const next = useCallback((e) => { e && e.stopPropagation(); setIndex((i) => (i + 1) % PHOTO_GALLERY.length); }, []);
  const prev = useCallback((e) => { e && e.stopPropagation(); setIndex((i) => (i - 1 + PHOTO_GALLERY.length) % PHOTO_GALLERY.length); }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, next, prev]);

  return (
    <section className="mx-auto max-w-7xl px-6 pb-32 lg:px-10" data-testid="photo-gallery">
      <Reveal className="mb-14 border-t border-border pt-16">
        <p className="overline text-sand-700">Stills</p>
        <h2 className="mt-6 font-display text-5xl text-sand-100 md:text-7xl">Photo gallery</h2>
      </Reveal>

      <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
        {PHOTO_GALLERY.map((p, i) => (
          <motion.button
            key={i}
            onClick={() => setIndex(i)}
            data-testid={`gallery-photo-${i}`}
            className="group relative block w-full overflow-hidden break-inside-avoid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
          >
            <img src={p.src} alt={`Desert Vibe Media still ${i + 1}`} loading="lazy" className="w-full object-cover grayscale-[25%] transition-all duration-700 group-hover:scale-[1.04] group-hover:grayscale-0" />
            <div className="absolute inset-0 bg-[#0D0B0A]/0 transition-colors duration-400 group-hover:bg-[#0D0B0A]/15" />
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0D0B0A]/95 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button onClick={close} className="absolute right-6 top-6 text-sand-100 transition-colors hover:text-sand-700" aria-label="Close" data-testid="lightbox-close"><X size={30} /></button>
            <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 text-sand-100 transition-colors hover:text-sand-700 md:left-10" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft size={40} /></button>
            <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 text-sand-100 transition-colors hover:text-sand-700 md:right-10" aria-label="Next" data-testid="lightbox-next"><ChevronRight size={40} /></button>

            <motion.figure
              key={index}
              className="max-h-[85vh] max-w-5xl"
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={PHOTO_GALLERY[index].src} alt={`Desert Vibe Media still ${index + 1}`} className="max-h-[85vh] w-auto object-contain" />
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PhotoGallery;
